import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Box,
	FormControl,
	FormHelperText,
	InputLabel,
	OutlinedInput,
	InputAdornment,
	Popover,
} from "@mui/material";
import { HexColorPicker } from "react-colorful";
import UtilsHelper from "src/utils/utils-helper";

type ColorPickerFieldProps = {
	defaultColor?: string;
	value?: string;
	onChange?: (color: string) => void;
	onBlur?: () => void;
	label: string;
	id: string;
	error?: boolean;
	helperText?: string;
	size?: "small" | "medium";
};

const ColorPickerField = ({
	defaultColor,
	value,
	onChange,
	onBlur,
	label,
	id,
	error,
	helperText,
	size,
}: ColorPickerFieldProps) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [inputValue, setInputValue] = useState<string>(
		value || defaultColor || ""
	);
	const isFocusedRef = useRef(false);

	const open = Boolean(anchorEl);

	useEffect(() => {
		if (isFocusedRef.current || open) {
			return;
		}

		setInputValue(value || defaultColor || "");
	}, [value, defaultColor, open]);

	const emitChange = useCallback(
		(newValue: string) => {
			if (onChange) {
				onChange(newValue);
			}
		},
		[onChange]
	);

	const handleSwatchClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => setAnchorEl(null);

	const handleInputChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		let nextValue = event.target.value.trim();

		if (
			nextValue &&
			!nextValue.startsWith("#") &&
			UtilsHelper.HexColorNoHashRegExp.test(nextValue)
		) {
			nextValue = `#${nextValue}`;
		}

		setInputValue(nextValue);
		emitChange(nextValue);
	};

	const handlePickerChange = useCallback(
		(newColor: string) => {
			setInputValue(newColor);
			emitChange(newColor);
		},
		[emitChange]
	);

	const handleFocus = () => {
		isFocusedRef.current = true;
	};

	const handleBlur = () => {
		isFocusedRef.current = false;
		let nextValue = inputValue.trim();

		if (
			nextValue &&
			!nextValue.startsWith("#") &&
			UtilsHelper.HexColorNoHashRegExp.test(nextValue)
		) {
			nextValue = `#${nextValue}`;
			setInputValue(nextValue);
			emitChange(nextValue);
		}

		if (onBlur) {
			onBlur();
		}
	};

	const swatchColor = UtilsHelper.HexColorRegExp.test(inputValue)
		? inputValue
		: defaultColor || "#ffffff";

	return (
		<FormControl fullWidth variant="outlined" error={error} size={size}>
			<InputLabel htmlFor={id} size={size === "small" ? "small" : "normal"}>{label}</InputLabel>
			<OutlinedInput
				id={id}
				label={label}
				value={inputValue}
				error={error}
				size={size}
				onChange={handleInputChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				startAdornment={
					<InputAdornment position="start">
						<Box
							onClick={handleSwatchClick}
							sx={{
								width: 24,
								height: 24,
								backgroundColor: swatchColor,
								borderRadius: "4px",
								border: "1px solid #ccc",
								cursor: "pointer",
							}}
						/>
					</InputAdornment>
				}
			/>
			{error && helperText && (
				<FormHelperText error>{helperText}</FormHelperText>
			)}

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<Box sx={{ p: 2 }}>
					<HexColorPicker
						color={swatchColor}
						onChange={handlePickerChange}
					/>
				</Box>
			</Popover>
		</FormControl>
	);
};

export default ColorPickerField;
